/**
 * `contracts/client-api.md` § The sequence, step 7: mutations against one entity run in a
 * per-entity queue, so a second quick edit waits for the first response and uses the
 * version it returned. Without this a user's own two edits would race and produce a
 * self-inflicted 412.
 *
 * The queue is also the tab's record of its own writes (2026-10-02, the owner's report: "it says
 * elie chamoun did a change while you were working on it… makes no sense if its my own name"). It
 * holds, per entity, the newest version this tab's own writes produced: the version each task's
 * response returned, and the version an echo of this tab's request carried for an entity no
 * response answered (a holder the request's derived fan-out bumped, the far item of a link).
 * A write is sent against the newer of that version and the version of the entity the screen
 * built the write on, so a version the cache learned since this tab's last write (another
 * person's change, the same person's change in another window) is never answered with the older
 * one, which is what raised a 412 naming whoever wrote last, most often the reader.
 */
export interface EntityQueue {
  /** Run `task` after every earlier task for `entityId` has settled, passing the newer of the
   * newest version this tab's own writes produced and `knownVersion`, the version of the entity
   * the write was built on. */
  run<T extends { version: number }>(entityId: string, knownVersion: number, task: (version: number) => Promise<T>): Promise<T>;
  /** Record a version this tab's own request produced that no task returned: the echo of a derived write. */
  observe(entityId: string, version: number): void;
  /** The newest version this tab's own writes produced for an entity. */
  latestVersion(entityId: string): number | null;
  /** Whether `version` is the newest version of the entity and this tab's own writes produced it. */
  produced(entityId: string, version: number): boolean;
  /** Forget every entity, on sign-out and on a restore discard. */
  clear(): void;
  pending(entityId: string): number;
}

export function createEntityQueue(): EntityQueue {
  const tails = new Map<string, Promise<unknown>>();
  const versions = new Map<string, number>();
  const counts = new Map<string, number>();
  const record = (entityId: string, version: number) => {
    versions.set(entityId, Math.max(versions.get(entityId) ?? version, version));
  };
  return {
    run(entityId, knownVersion, task) {
      const previous = tails.get(entityId) ?? Promise.resolve();
      counts.set(entityId, (counts.get(entityId) ?? 0) + 1);
      const next = previous
        .catch(() => undefined)
        .then(async () => {
          const result = await task(Math.max(versions.get(entityId) ?? knownVersion, knownVersion));
          record(entityId, result.version);
          return result;
        })
        .finally(() => {
          counts.set(entityId, (counts.get(entityId) ?? 1) - 1);
          if (tails.get(entityId) === next) tails.delete(entityId);
        });
      tails.set(entityId, next);
      return next;
    },
    observe: record,
    latestVersion: (entityId) => versions.get(entityId) ?? null,
    produced: (entityId, version) => versions.get(entityId) === version,
    clear() {
      tails.clear();
      versions.clear();
      counts.clear();
    },
    pending: (entityId) => counts.get(entityId) ?? 0,
  };
}
